import {
  BASIS_LABELS,
  CHART_CAPTIONS,
  PURPOSE_LABELS,
  type Engagement,
  type ReportChart,
  type ValuationReport,
} from '@ave/shared';
import { dateTime, longDate, money } from '../lib/format';
import { Badge, Card, EmptyState, SectionHeading } from './ui';
import { Icon } from './icons';
import {
  ChartData,
  ComparableMultiplesChart,
  DiscountEvidenceChart,
  DiscountSensitivityHeatmap,
  EarningsWeightingChart,
  Figure,
  LatentTaxPositionChart,
  LatentTaxSensitivityChart,
  NavCompositionChart,
  ProvenanceSummaryChart,
  TransactionTimelineChart,
} from './charts';
import { ValuationBridge, buildSegments } from './ValuationBridge';
import { ChartBoundary } from './ChartBoundary';

const CHART_SOURCES: Partial<Record<ReportChart, string>> = {
  latent_tax_position:
    'Source: calculation log — latent tax run. Corridor from the assumption corridors in force at the valuation date.',
  latent_tax_sensitivity:
    'Source: calculation log — holding sensitivity run. Each point is a full recalculation, not an interpolation.',
  discount_evidence:
    'Sources: published restricted-stock and pre-IPO studies, and control-premium data, held in the market evidence layer.',
  discount_sensitivity:
    'Source: calculation log — holding sensitivity run. Every cell is a separate calculation through the same discount functions.',
  comparable_multiples: 'Source: comparable company set, computed from filings and announced deal metrics.',
  valuation_bridge: 'Source: calculation log. Every bar is a calculation step.',
  nav_composition: 'Source: calculation log — net asset value assembly.',
  earnings_weighting: 'Source: calculation log — maintainable earnings run.',
  transaction_timeline: 'Source: SH01 filings and company records, recorded in the evidence layer.',
  provenance_summary: 'Source: provenance tags carried by every fact recorded on the engagement.',
};

/**
 * Report preview.
 *
 * Drafts carry the watermark until the release gate passes — the PI
 * posture, not decoration. Exhibits are interleaved with the narrative
 * they justify, because a discount stated in prose and an evidence range
 * shown three pages later is how a reader ends up taking a number on
 * trust.
 */
export function ReportPreview({
  report,
  engagement,
  chartData,
  entityName,
}: {
  report: ValuationReport | null;
  engagement: Engagement;
  chartData: ChartData;
  entityName: string;
}) {
  if (!report) {
    return (
      <Card>
        <SectionHeading title="Report" />
        <EmptyState
          title="No draft yet"
          detail="The draft is generated section by section against the Acumon templates once the calculations are complete. Figures interpolate from the calculation log, standards claims carry citations, and each exhibit plots the engine's own numbers."
        />
      </Card>
    );
  }

  const isDraft = report.status === 'draft';
  const emptyMandatory = report.sections.filter((s) => s.mandatory && !s.body.trim());

  // Figures are numbered once, across the whole document.
  let figureNo = 0;
  const numbered = report.sections.map((section) => ({
    section,
    charts: (section.charts ?? []).map((chart) => ({ chart, number: ++figureNo })),
  }));

  return (
    <div className="space-y-4">
      <Card>
        <SectionHeading
          title={isDraft ? 'Draft report' : 'Released report'}
          subtitle={`Version ${report.version} · ${report.sections.length} sections · ${figureNo} exhibits · ${report.figureBindings.length} figures bound to the calculation log`}
          right={
            <div className="flex items-center gap-3">
              {isDraft ? (
                <Badge tone="amber">
                  <Icon name="alert" className="h-3 w-3" strokeWidth={2} />
                  Draft — not released
                </Badge>
              ) : (
                <Badge tone="emerald">
                  <Icon name="check" className="h-3 w-3" strokeWidth={2.5} />
                  Released {report.releasedAt ? dateTime(report.releasedAt) : ''}
                </Badge>
              )}
              <button
                type="button"
                className="btn-secondary no-print text-xs"
                onClick={() => window.print()}
                title="Print, or save as PDF. The draft watermark prints with it."
              >
                <Icon name="document" className="h-3.5 w-3.5" />
                Print
              </button>
              {/* The deliverable of the engagement is a document, and the
                  browser's print dialogue is an interim answer to that. Both
                  formats carry the draft watermark on every page: a file,
                  unlike a screen, gets forwarded. */}
              {(['docx', 'pdf'] as const).map((format) => (
                <a
                  key={format}
                  className="btn-secondary no-print text-xs"
                  href={`/api/engagements/${engagement.id}/reports/${report.id}/render.${format}`}
                  title={
                    isDraft
                      ? 'Downloads watermarked on every page until the review gate is passed.'
                      : 'The released report, with every figure and the step it came from.'
                  }
                >
                  <Icon name="archive" className="h-3.5 w-3.5" />
                  {format.toUpperCase()}
                </a>
              ))}
            </div>
          }
        />

        {emptyMandatory.length > 0 ? (
          <div className="border-b border-amber-200 bg-amber-50 px-6 py-3">
            <p className="text-sm text-amber-900">
              <span className="font-semibold">
                {emptyMandatory.length} mandatory section
                {emptyMandatory.length === 1 ? '' : 's'} could not be built:
              </span>{' '}
              {emptyMandatory.map((s) => s.heading).join(', ')}. A mandatory section with nothing to
              say means the engagement is not ready, not that the section may be dropped.
            </p>
          </div>
        ) : null}

        <div className="print-page relative bg-slate-100/60 px-4 py-6 sm:px-8 sm:py-10">
          <article className="print-document relative mx-auto max-w-3xl bg-white px-7 py-10 shadow-sm ring-1 ring-slate-200 sm:px-12">
            {isDraft ? (
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 z-10 flex select-none items-start justify-center overflow-hidden pt-52"
              >
                <span className="-rotate-[26deg] whitespace-nowrap text-[56px] font-bold tracking-widest text-slate-900/[0.05]">
                  DRAFT — NOT RELEASED
                </span>
              </div>
            ) : null}

            {/* Cover */}
            <header className="border-b border-slate-200 pb-6">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-teal-700">
                Valuation report
              </p>
              <h2 className="mt-2 text-2xl font-semibold leading-tight tracking-tight text-slate-900">
                {entityName}
              </h2>
              <p className="mt-1 text-sm text-slate-500">{engagement.name}</p>

              <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div>
                  <dt className="text-xs text-slate-400">Purpose</dt>
                  <dd className="text-slate-800">{PURPOSE_LABELS[engagement.purpose]}</dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-400">Basis of value</dt>
                  <dd className="text-slate-800">{BASIS_LABELS[engagement.basisOfValue]}</dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-400">Valuation date</dt>
                  <dd className="tnum text-slate-800">{longDate(engagement.valuationDate)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-400">Our reference</dt>
                  <dd className="font-mono text-slate-800">{engagement.ref}</dd>
                </div>
              </dl>
            </header>

            {/* Contents */}
            <nav className="border-b border-slate-100 py-5">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Contents
              </p>
              <ol className="space-y-1">
                {report.sections.map((s, i) => (
                  <li key={s.key} className="flex justify-between gap-4 text-sm text-slate-600">
                    <span>
                      {i + 1}. {s.heading}
                    </span>
                  </li>
                ))}
              </ol>
            </nav>

            {/* Body */}
            {numbered.map(({ section, charts }, i) => (
              <section key={section.key} className="pt-7">
                <div className="mb-2.5 flex flex-wrap items-baseline gap-2">
                  <h3 className="text-[17px] font-semibold text-slate-900">
                    {i + 1}. {section.heading}
                  </h3>
                  {section.citations.map((c) => (
                    <Badge key={c} tone="violet">
                      {c}
                    </Badge>
                  ))}
                </div>

                <div className="report-prose">
                  {section.body.trim() ? (
                    section.body
                      .split('\n\n')
                      .map((para, pi) => <p key={pi} dangerouslySetInnerHTML={{ __html: bold(para) }} />)
                  ) : (
                    <p className="italic text-amber-700">
                      Not yet available — this section is mandatory for the engagement’s purpose.
                    </p>
                  )}
                </div>

                {charts.map(({ chart, number }) => (
                  <Figure
                    key={chart}
                    number={number}
                    title={CHART_CAPTIONS[chart]}
                    source={CHART_SOURCES[chart]}
                  >
                    <ChartBoundary label={chart}>
                      <ChartFor chart={chart} data={chartData} />
                    </ChartBoundary>
                  </Figure>
                ))}
              </section>
            ))}

            <footer className="mt-10 border-t border-slate-200 pt-5">
              <p className="text-xs leading-relaxed text-slate-400">
                Every figure in this report is bound to an entry in the calculation log and is
                reproducible from the archived engagement file. The standards and guidance applied
                are those in force at the valuation date.
              </p>
            </footer>
          </article>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <SectionHeading
            title="Figure bindings"
            subtitle="A report figure without a calculation-step pointer fails the build"
          />
          {report.figureBindings.length === 0 ? (
            <EmptyState title="No bound figures" />
          ) : (
            <ul className="divide-y divide-slate-100">
              {report.figureBindings.map((b) => (
                <li key={b.token} className="flex items-center gap-3 px-6 py-2.5">
                  <Icon name="calculator" className="h-4 w-4 shrink-0 text-slate-300" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-slate-800">{b.label}</span>
                    <span className="block font-mono text-[11px] text-slate-400">
                      step {b.calcStepId.slice(0, 8)}
                    </span>
                  </span>
                  <span className="tnum text-sm font-medium text-slate-900">
                    {money(b.value, engagement.currency)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <SectionHeading
            title="Standards corpus pinned"
            subtitle="The standards as they stood at the valuation date, not at run date"
          />
          {report.corpusPins.length === 0 ? (
            <EmptyState title="No corpus versions pinned" />
          ) : (
            <ul className="divide-y divide-slate-100">
              {report.corpusPins.map((p) => (
                <li key={`${p.source}-${p.versionLabel}`} className="px-6 py-2.5">
                  <p className="text-sm text-slate-800">{p.source}</p>
                  <p className="text-xs text-slate-400">
                    {p.versionLabel} · in force from {p.effectiveFrom}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function ChartFor({ chart, data }: { chart: ReportChart; data: ChartData }) {
  switch (chart) {
    case 'nav_composition':
      return <NavCompositionChart data={data} />;
    case 'valuation_bridge': {
      const outputs = (fn: string) =>
        [...data.runs].reverse().find((r) => r.functionName === fn && r.status === 'ok')
          ?.outputs as Record<string, string> | undefined;
      return (
        <ValuationBridge
          segments={buildSegments({
            nav: outputs('nav_assembly'),
            bridge: outputs('enterprise_to_equity'),
            holding: outputs('holding_value'),
          })}
        />
      );
    }
    case 'latent_tax_position':
      return <LatentTaxPositionChart data={data} />;
    case 'latent_tax_sensitivity':
      return <LatentTaxSensitivityChart data={data} />;
    case 'discount_evidence':
      return <DiscountEvidenceChart data={data} />;
    case 'discount_sensitivity':
      return <DiscountSensitivityHeatmap data={data} />;
    case 'earnings_weighting':
      return <EarningsWeightingChart data={data} />;
    case 'comparable_multiples':
      return <ComparableMultiplesChart data={data} />;
    case 'transaction_timeline':
      return <TransactionTimelineChart data={data} />;
    case 'provenance_summary':
      return <ProvenanceSummaryChart data={data} />;
    default:
      return null;
  }
}

/** The templates emit **bold** for defined terms; nothing else is markup. */
function bold(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
}
